// ObjectsList.jsx - Grid view of museum objects with filtering
import React, { useState, useMemo } from 'react';
import {
  Search, Filter, Plus, Edit, Trash2, Eye, EyeOff, Star, MapPin, Tag,
  RefreshCw, Hash, Image as ImageIcon, LayoutGrid, List
} from 'lucide-react';

const ObjectsList = ({
  objects,
  museum,
  isLoading,
  filters,
  onFilterChange,
  onAddObject,
  onEditObject,
  onDeleteObject,
  onRefresh
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [showFilters, setShowFilters] = useState(false);

  // Local filter for search (client-side filtering for immediate response)
  const filteredObjects = useMemo(() => {
    if (!searchTerm.trim()) return objects;

    const term = searchTerm.toLowerCase();
    return objects.filter(obj =>
      obj.name?.toLowerCase().includes(term) ||
      obj.description?.toLowerCase().includes(term) ||
      obj.artist?.toLowerCase().includes(term) ||
      obj.inventoryNumber?.toLowerCase().includes(term) ||
      obj.objectNumber?.toString().includes(term)
    );
  }, [objects, searchTerm]);

  // Handle room filter change
  const handleRoomFilter = (roomId) => {
    onFilterChange({ room: roomId });
  };

  // Handle category filter change
  const handleCategoryFilter = (category) => {
    onFilterChange({ category });
  };

  // Handle delete with confirmation
  const handleDelete = async (object) => {
    if (!window.confirm(`Delete "${object.name}"?\n\nThis action cannot be undone.`)) {
      return;
    }
    onDeleteObject(object.id);
  };

  // Get room name from ID
  const getRoomName = (roomId) => {
    if (!roomId) return 'Unassigned';
    const room = museum?.rooms?.find(r => r.id === roomId);
    return room?.name || roomId;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">
            Museum Objects
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({filteredObjects.length} {filteredObjects.length === 1 ? 'object' : 'objects'})
            </span>
          </h2>
        </div>

        <div className="flex items-center gap-3">
          {/* Refresh Button */}
          <button
            onClick={onRefresh}
            disabled={isLoading}
            className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
          </button>

          {/* View Mode Toggle */}
          <div className="flex border border-gray-300 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 ${viewMode === 'grid' ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
              title="Grid view"
            >
              <LayoutGrid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 ${viewMode === 'list' ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
              title="List view"
            >
              <List className="w-5 h-5" />
            </button>
          </div>

          {/* Add Object Button */}
          <button
            onClick={onAddObject}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Object
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, description, artist, or number..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Room Filter */}
          <select
            value={filters.room || ''}
            onChange={(e) => handleRoomFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Rooms</option>
            {museum?.rooms?.map(room => (
              <option key={room.id} value={room.id}>
                {room.name}
              </option>
            ))}
          </select>

          {/* Category Filter */}
          <select
            value={filters.category || ''}
            onChange={(e) => handleCategoryFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Categories</option>
            {museum?.categories?.map(cat => (
              <option key={cat} value={cat}>
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Objects Grid/List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-600">Loading objects...</span>
        </div>
      ) : filteredObjects.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">
            {searchTerm || filters.room || filters.category
              ? 'No objects match your filters'
              : 'No objects in this museum yet'}
          </p>
          {!searchTerm && !filters.room && !filters.category && (
            <button
              onClick={onAddObject}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors inline-flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Add First Object
            </button>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        /* Grid View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredObjects.map(object => (
            <div
              key={object.id}
              className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow ${
                !object.visible ? 'opacity-60' : ''
              }`}
            >
              {/* Image */}
              <div className="relative h-40 bg-gray-100">
                {object.photoUrl ? (
                  <img
                    src={object.photoUrl}
                    alt={object.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="w-12 h-12 text-gray-300" />
                  </div>
                )}

                {/* Object Number Badge */}
                <div className="absolute top-2 left-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-sm font-bold">
                  #{object.objectNumber}
                </div>

                {/* Status Badges */}
                <div className="absolute top-2 right-2 flex gap-1">
                  {object.featured && (
                    <span className="bg-yellow-500 text-white p-1 rounded" title="Featured">
                      <Star className="w-4 h-4" />
                    </span>
                  )}
                  {!object.visible && (
                    <span className="bg-gray-500 text-white p-1 rounded" title="Hidden">
                      <EyeOff className="w-4 h-4" />
                    </span>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="p-4">
                <h3 className="font-semibold text-gray-800 line-clamp-1 mb-1">
                  {object.name}
                </h3>

                <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                  {object.description}
                </p>

                {/* Meta Info */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {object.room && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded">
                      <MapPin className="w-3 h-3" />
                      {getRoomName(object.room)}
                    </span>
                  )}
                  {object.category && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded capitalize">
                      <Tag className="w-3 h-3" />
                      {object.category}
                    </span>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => onEditObject(object)}
                    className="flex-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors text-sm flex items-center justify-center gap-1"
                  >
                    <Edit className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(object)}
                    className="px-3 py-1.5 bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors text-sm"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* List View */
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  #
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Object
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Room
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredObjects.map(object => (
                <tr
                  key={object.id}
                  className={`hover:bg-gray-50 ${!object.visible ? 'opacity-60' : ''}`}
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="font-bold text-gray-700">#{object.objectNumber}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {object.photoUrl ? (
                        <img
                          src={object.photoUrl}
                          alt={object.name}
                          className="w-10 h-10 rounded object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center">
                          <ImageIcon className="w-5 h-5 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-gray-800">{object.name}</p>
                        {object.artist && (
                          <p className="text-sm text-gray-500">{object.artist}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                    {getRoomName(object.room)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {object.category && (
                      <span className="px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded capitalize">
                        {object.category}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex gap-1">
                      {object.featured && (
                        <Star className="w-4 h-4 text-yellow-500" title="Featured" />
                      )}
                      {object.visible ? (
                        <Eye className="w-4 h-4 text-green-500" title="Visible" />
                      ) : (
                        <EyeOff className="w-4 h-4 text-gray-400" title="Hidden" />
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => onEditObject(object)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(object)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ObjectsList;
